/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/assistantOperatorMode.ts
 * PURPOSE: Operator mode — "do it" confirm + pending recommended step from action-first streams.
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-09 [2.2.1494] — Explicit operator-choice chip labels (calendar / task / activity).
 * RECENT_PRIOR: 2026-07-08 [2.2.1485] — Sprint 2 operator confirm after action-first replies.
 */

import type { StreamSuggestedAction } from './streamSuggestedActions';

/** Short affirmations that execute the last recommended operator step. */
export function userLineImpliesOperatorConfirm(line: string): boolean {
  const t = line.trim().toLowerCase();
  if (!t) return false;
  const exact = new Set([
    'yes',
    'yeah',
    'yep',
    'y',
    'ok',
    'okay',
    'sure',
    'confirm',
    'proceed',
    'go ahead',
    'do it',
    "do it.",
    'run it',
    'execute',
  ]);
  if (exact.has(t)) return true;
  return /\b(yes|yeah|yep|ok|okay|sure|confirm|proceed|go\s*ahead|do\s*it|run\s*it)\b/i.test(t);
}

export function parseOperatorPendingPromptFromMetadata(metadata: unknown): string | undefined {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return undefined;
  }
  const raw = (metadata as Record<string, unknown>).operator_pending_prompt;
  if (typeof raw !== 'string') return undefined;
  const text = raw.trim();
  return text || undefined;
}

/**
 * When the user confirms with "do it", run the pending recommended step instead.
 * Returns the outbound message to send (may differ from what the user typed).
 */
export function resolveOperatorOutboundMessage(
  userText: string,
  pendingPrompt: string | null | undefined,
): string {
  const trimmed = userText.trim();
  const pending = (pendingPrompt || '').trim();
  if (pending && userLineImpliesOperatorConfirm(trimmed)) {
    return pending;
  }
  return trimmed;
}

export type AssistantFollowUpChipItem = {
  label: string;
  prompt: string;
  executionMode: 'prompt' | 'advisor_execute';
  /** Primary operator chip — emphasized as one-click execute. */
  isOperatorPrimary?: boolean;
};

export function followUpChipFromStreamAction(
  action: StreamSuggestedAction,
  index: number,
): AssistantFollowUpChipItem {
  const prompt = (action.prompt_text || action.label || '').trim();
  const modeRaw = (action.execution_mode || 'prompt').toLowerCase();
  const executionMode = modeRaw === 'advisor_execute' ? 'advisor_execute' : 'prompt';
  const isOperatorChoice = action.source === 'action_first_operator_choice';
  const isLegacyPrimary =
    index === 0 &&
    !isOperatorChoice &&
    (executionMode === 'advisor_execute' ||
      action.source === 'action_first_primary' ||
      action.action?.startsWith('action_first_'));
  const isOperatorChip =
    isOperatorChoice ||
    executionMode === 'advisor_execute' ||
    action.source === 'action_first_primary' ||
    (typeof action.action === 'string' && action.action.startsWith('action_first_'));
  const label =
    isLegacyPrimary && isOperatorChip
      ? 'Do it'
      : (action.label || prompt).trim();
  return {
    label: label || prompt,
    prompt,
    executionMode,
    isOperatorPrimary: isLegacyPrimary && isOperatorChip,
  };
}
